import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Editor from '@monaco-editor/react'

import { updateConfig } from '../api/config'
import LoadingSpinner from '../components/LoadingSpinner'

const CustomStyles = ({ css, js }) => {
  const [activeTab, setActiveTab] = useState('css')
  const [cssContent, setCssContent] = useState(css || '')
  const [jsContent, setJsContent] = useState(js || '')

  const queryClient = useQueryClient()

  const updateCssMutation = useMutation({
    mutationFn: (data) => updateConfig('customCss', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('CSS 样式已更新')
    },
    onError: (error) => {
      toast.error(`CSS 更新失败: ${error.message}`)
    },
  })

  const updateJsMutation = useMutation({
    mutationFn: (data) => updateConfig('customJs', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('JavaScript 代码已更新')
    },
    onError: (error) => {
      toast.error(`JavaScript 更新失败: ${error.message}`)
    },
  })

  const handleSaveCss = () => {
    updateCssMutation.mutate(cssContent)
  }

  const handleSaveJs = () => {
    updateJsMutation.mutate(jsContent)
  }

  const handleReset = () => {
    if (activeTab === 'css') {
      setCssContent(css || '')
    } else {
      setJsContent(js || '')
    }
  }

  const editorOptions = {
    minimap: { enabled: false },
    fontSize: 14,
    lineNumbers: 'on',
    roundedSelection: false,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    theme: 'vs-dark',
    wordWrap: 'on',
    tabSize: 2,
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-3xl font-bold text-white">自定义样式</h1>
        <p className="mt-2 text-gray-400">
          编辑自定义 CSS 样式和 JavaScript 代码来个性化你的导航页
        </p>
      </div>

      {/* 标签页 */}
      <div className="card">
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('css')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'css'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            CSS 样式
          </button>
          <button
            onClick={() => setActiveTab('js')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'js'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            JavaScript 代码
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'css' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  自定义 CSS 样式
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={handleReset}
                    className="btn-secondary"
                  >
                    重置
                  </button>
                  <button
                    onClick={handleSaveCss}
                    disabled={updateCssMutation.isLoading}
                    className="btn-primary"
                  >
                    {updateCssMutation.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        保存中...
                      </>
                    ) : (
                      '保存 CSS'
                    )}
                  </button>
                </div>
              </div>

              <div className="border border-gray-600 rounded-lg overflow-hidden">
                <Editor
                  height="500px"
                  language="css"
                  value={cssContent}
                  onChange={(value) => setCssContent(value || '')}
                  options={editorOptions}
                />
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-white mb-2">CSS 使用提示</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• 使用 CSS 变量来定义颜色主题：<code className="text-gray-300">:root {'{ --primary-color: #3b82f6; }'}</code></li>
                  <li>• 可以覆盖 Homepage 的默认样式来自定义外观</li>
                  <li>• 建议使用类选择器而不是 ID 选择器</li>
                  <li>• 可以使用媒体查询来适配不同屏幕尺寸</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'js' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  自定义 JavaScript 代码
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={handleReset}
                    className="btn-secondary"
                  >
                    重置
                  </button>
                  <button
                    onClick={handleSaveJs}
                    disabled={updateJsMutation.isLoading}
                    className="btn-primary"
                  >
                    {updateJsMutation.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        保存中...
                      </>
                    ) : (
                      '保存 JavaScript'
                    )}
                  </button>
                </div>
              </div>

              <div className="border border-gray-600 rounded-lg overflow-hidden">
                <Editor
                  height="500px"
                  language="javascript"
                  value={jsContent}
                  onChange={(value) => setJsContent(value || '')}
                  options={editorOptions}
                />
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-white mb-2">JavaScript 使用提示</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• 代码将在页面加载完成后执行</li>
                  <li>• 可以使用现代 JavaScript 语法 (ES6+)</li>
                  <li>• 避免使用可能影响页面性能的代码</li>
                  <li>• 可以添加自定义事件监听器和交互功能</li>
                  <li>• 请谨慎使用，错误的代码可能影响页面正常运行</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 预设样式模板 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">预设样式模板</h3>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">毛玻璃效果</h4>
              <p className="text-sm text-gray-400 mb-3">
                为卡片添加现代化的毛玻璃背景效果
              </p>
              <button
                onClick={() => {
                  const template = `/* 毛玻璃效果 */
.service-card, .widget {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">动画效果</h4>
              <p className="text-sm text-gray-400 mb-3">
                为元素添加淡入和悬停动画效果
              </p>
              <button
                onClick={() => {
                  const template = `/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.service-card {
  animation: fadeInUp 0.6s ease-out;
  transition: transform 0.3s ease;
}

.service-card:hover {
  transform: translateY(-5px);
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">自定义滚动条</h4>
              <p className="text-sm text-gray-400 mb-3">
                美化页面滚动条样式
              </p>
              <button
                onClick={() => {
                  const template = `/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #3b82f6;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #2563eb;
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            {/* 白色文字描边 */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">白色文字描边</h4>
              <p className="text-sm text-gray-400 mb-3">
                为文字添加黑色描边，增强在亮色背景下的可读性。
              </p>
              <button
                onClick={() => {
                  const template = `/* 白色文字描边 */
/* 选择所有显示文本的元素，您可能需要根据实际页面调整选择器 */
.app-name, .service-title, .description {
    color: white !important; /* 设置文字颜色为白色 */
    text-shadow:
        -1px -1px 0 #000,
        1px -1px 0 #000,
        -1px 1px 0 #000,
        1px 1px 0 #000 !important; /* 通过四个方向的阴影模拟黑色描边 */
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            {/* 黑色文字描边 */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">黑色文字描边</h4>
              <p className="text-sm text-gray-400 mb-3">
                为文字添加白色描边，增强在暗色背景下的可读性。
              </p>
              <button
                onClick={() => {
                  const template = `/* 黑色文字描边 */
/* 选择所有显示文本的元素 */
.app-name, .service-title, .description {
    color: black !important; /* 设置文字颜色为黑色 */
    text-shadow:
        -1px -1px 0 #fff,
        1px -1px 0 #fff,
        -1px 1px 0 #fff,
        1px 1px 0 #fff !important; /* 通过四个方向的阴影模拟白色描边 */
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            {/* 隐藏页头 */}
            {/* 紧凑布局 */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">紧凑布局</h4>
              <p className="text-sm text-gray-400 mb-3">
                减少元素间距，在屏幕上显示更多内容。
              </p>
              <button
                onClick={() => {
                  const template = `/* 紧凑布局 */
:root {
  --widget-padding: 0.5rem;
  --card-padding: 0.5rem;
}
.layout-container {
  gap: 0.5rem;
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            {/* 透明卡片 */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">透明卡片</h4>
              <p className="text-sm text-gray-400 mb-3">
                移除卡片背景和边框，适合极简主题。
              </p>
              <button
                onClick={() => {
                  const template = `/* 透明卡片 */
.service-card, .widget {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>

            {/* 聚焦效果 */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">聚焦效果</h4>
              <p className="text-sm text-gray-400 mb-3">
                鼠标悬停时，其他服务将变暗，突出当前项。
              </p>
              <button
                onClick={() => {
                  const template = `/* 聚焦效果 */
.services-container:has(.service-card:hover) .service-card:not(:hover) {
    filter: grayscale(100%);
    opacity: 0.6;
    transition: all 0.3s ease;
}`
                  setCssContent(cssContent + '\n\n' + template)
                }}
                className="btn-secondary btn-sm"
              >
                添加到 CSS
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomStyles
